import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdAdd, MdArrowBack, MdDelete, MdRefresh, MdSend } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import TemplatesFallbackNotice from 'views/backoffice/whatsappDiffusion/TemplatesFallbackNotice';
import {
    APPROVAL_STATUS,
    TemplatesFallback,
    WhatsappTemplate,
    extractPlaceholders,
    getApprovalStatus,
} from 'views/backoffice/whatsappDiffusion/types';

const CATEGORIES = [
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'UTILITY', label: 'Utilitaire' },
    { value: 'AUTHENTICATION', label: 'Authentification' },
];

const LANGUAGES = [
    { value: 'fr', label: 'Français' },
    { value: 'en', label: 'Anglais' },
];

const PENDING_STATUSES = ['pending', 'received'];

const inputClass =
    'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-green-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white';

export default function BackOfficeWhatsappTemplates() {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [templatesFallback, setTemplatesFallback] = useState<TemplatesFallback>({ fallback: false });

    // Create form
    const [showCreate, setShowCreate] = useState(false);
    const [name, setName] = useState('');
    const [language, setLanguage] = useState('fr');
    const [category, setCategory] = useState('MARKETING');
    const [body, setBody] = useState('');
    const [samples, setSamples] = useState<Record<string, string>>({});
    const [submitNow, setSubmitNow] = useState(true);
    const [creating, setCreating] = useState(false);

    // Row actions
    const [submittingSid, setSubmittingSid] = useState<string | null>(null);
    const [submitCategory, setSubmitCategory] = useState<Record<string, string>>({});
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchTemplates = async (silent = false) => {
        try {
            if (silent) setRefreshing(true);
            else setLoading(true);
            const res = await axios.get(`${CONFIG.apiUrl}/admin/whatsapp-templates`, { headers });
            setTemplates(res.data.templates);
            setTemplatesFallback({ fallback: !!res.data.fallback, fallbackReason: res.data.fallbackReason });
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Poll while some templates are awaiting WhatsApp review
    const hasPending = templates.some((t) => PENDING_STATUSES.includes(t.approval?.status || ''));
    useEffect(() => {
        if (!hasPending) return;
        const interval = setInterval(() => fetchTemplates(true), 60000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPending]);

    const counts = useMemo(() => {
        const c: Record<string, number> = {};
        templates.forEach((t) => {
            const s = t.approval?.status || 'unsubmitted';
            c[s] = (c[s] || 0) + 1;
        });
        return c;
    }, [templates]);

    const filtered = useMemo(
        () =>
            statusFilter === 'all'
                ? templates
                : templates.filter((t) => (t.approval?.status || 'unsubmitted') === statusFilter),
        [templates, statusFilter]
    );

    const placeholders = extractPlaceholders(body);

    const resetForm = () => {
        setName('');
        setLanguage('fr');
        setCategory('MARKETING');
        setBody('');
        setSamples({});
        setSubmitNow(true);
    };

    const insertVariable = () => {
        const next = placeholders.length > 0 ? Math.max(...placeholders.map(Number).filter((n) => !isNaN(n))) + 1 : 1;
        setBody((prev) => `${prev}{{${next}}}`);
    };

    const handleCreate = async () => {
        if (!name || !body.trim()) return;
        if (placeholders.some((k) => !samples[k]?.trim())) {
            alert('WhatsApp exige un exemple pour chaque variable.');
            return;
        }
        try {
            setCreating(true);
            const variables: Record<string, string> = {};
            placeholders.forEach((k) => { variables[k] = samples[k]; });
            await axios.post(
                `${CONFIG.apiUrl}/admin/whatsapp-templates`,
                { friendlyName: name, language, body, variables, category, submit: submitNow },
                { headers }
            );
            resetForm();
            setShowCreate(false);
            fetchTemplates(true);
        } catch (e: any) {
            console.error(e);
            alert(e?.response?.data?.message || 'Échec de la création');
        } finally {
            setCreating(false);
        }
    };

    const handleSubmit = async (tpl: WhatsappTemplate) => {
        const cat = submitCategory[tpl.sid] || 'MARKETING';
        try {
            setSubmittingSid(tpl.sid);
            await axios.post(`${CONFIG.apiUrl}/admin/whatsapp-templates/${tpl.sid}/submit`, { category: cat }, { headers });
            fetchTemplates(true);
        } catch (e: any) {
            console.error(e);
            alert(e?.response?.data?.message || 'Échec de la soumission');
        } finally {
            setSubmittingSid(null);
        }
    };

    const handleDelete = async (sid: string) => {
        try {
            await axios.delete(`${CONFIG.apiUrl}/admin/whatsapp-templates/${sid}`, { headers });
            setTemplates((prev) => prev.filter((t) => t.sid !== sid));
            setDeleteConfirm(null);
        } catch (e: any) {
            console.error(e);
            alert(e?.response?.data?.message || 'Échec de la suppression');
        }
    };

    const preview = body.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => samples[k] || `{{${k}}}`);

    return (
        <div className="pt-[50px]">
            <button
                onClick={() => navigate('/back-office/whatsapp-diffusion')}
                className="mb-3 flex items-center gap-1 text-sm text-gray-500 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white"
            >
                <MdArrowBack /> Listes diffusion
            </button>

            {/* Header */}
            <Card extra="p-4 mb-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <FaWhatsapp className="text-2xl text-green-500" />
                        <span className="text-lg font-bold text-navy-700 dark:text-white">Templates WhatsApp</span>
                        {!loading && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-navy-700 dark:text-gray-300">
                                {templates.length}
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => fetchTemplates(true)}
                            disabled={refreshing}
                            className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
                        >
                            <MdRefresh className={refreshing ? 'animate-spin' : ''} />
                            Actualiser
                        </button>
                        <button
                            onClick={() => setShowCreate(!showCreate)}
                            className="flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600"
                        >
                            <MdAdd /> Nouveau template
                        </button>
                    </div>
                </div>

                {templatesFallback.fallback && (
                    <TemplatesFallbackNotice reason={templatesFallback.fallbackReason} className="mt-4" />
                )}

                {/* Status filter */}
                {!loading && templates.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        <button
                            onClick={() => setStatusFilter('all')}
                            className={`rounded-full px-3 py-1 text-xs font-medium ${
                                statusFilter === 'all'
                                    ? 'bg-navy-700 text-white dark:bg-white dark:text-navy-700'
                                    : 'bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300'
                            }`}
                        >
                            Tous ({templates.length})
                        </button>
                        {Object.entries(counts).map(([status, n]) => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`rounded-full px-3 py-1 text-xs font-medium ${
                                    APPROVAL_STATUS[status]?.className || 'bg-gray-100 text-gray-500'
                                } ${statusFilter === status ? 'ring-2 ring-navy-700 dark:ring-white' : ''}`}
                            >
                                {APPROVAL_STATUS[status]?.label || status} ({n})
                            </button>
                        ))}
                    </div>
                )}

                {/* Create form */}
                {showCreate && (
                    <div className="mt-4 grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-800 lg:grid-cols-2">
                        <div className="space-y-3">
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Nom (minuscules, chiffres, _)
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''))}
                                    placeholder="nouveaux_leads_novembre"
                                    className={inputClass}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Langue</label>
                                    <select value={language} onChange={(e) => setLanguage(e.target.value)} className={inputClass}>
                                        {LANGUAGES.map((l) => (
                                            <option key={l.value} value={l.value}>{l.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Catégorie</label>
                                    <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
                                        {CATEGORIES.map((c) => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <div className="mb-1 flex items-center justify-between">
                                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Message</label>
                                    <button onClick={insertVariable} className="text-xs text-brand-500 hover:underline">
                                        + Variable
                                    </button>
                                </div>
                                <textarea
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    rows={6}
                                    placeholder="Bonjour {{1}}, de nouveaux leads sont disponibles sur Leadrs..."
                                    className={inputClass}
                                />
                            </div>
                            {placeholders.length > 0 && (
                                <div className="space-y-2">
                                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                        Exemples de valeurs (requis par WhatsApp)
                                    </div>
                                    {placeholders.map((k) => (
                                        <div key={k} className="flex items-center gap-2">
                                            <span className="w-12 shrink-0 font-mono text-xs text-gray-500">{`{{${k}}}`}</span>
                                            <input
                                                type="text"
                                                value={samples[k] ?? ''}
                                                onChange={(e) => setSamples((prev) => ({ ...prev, [k]: e.target.value }))}
                                                className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-green-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={submitNow}
                                    onChange={(e) => setSubmitNow(e.target.checked)}
                                    className="accent-green-500"
                                />
                                Soumettre directement à WhatsApp pour approbation
                            </label>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleCreate}
                                    disabled={!name || !body.trim() || creating}
                                    className="flex items-center gap-1 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-50"
                                >
                                    {creating ? 'Création...' : submitNow ? 'Créer et soumettre' : 'Créer'}
                                </button>
                                <button
                                    onClick={() => { resetForm(); setShowCreate(false); }}
                                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300"
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                        <div>
                            <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Aperçu</div>
                            <div className="rounded-lg bg-[#efeae2] p-4 dark:bg-navy-900">
                                <div className="max-w-[90%] whitespace-pre-wrap rounded-lg rounded-tl-none bg-white p-3 text-sm text-gray-800 shadow-sm dark:bg-navy-700 dark:text-gray-100">
                                    {preview || <span className="text-gray-400">Votre message…</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            {/* Templates grid */}
            {loading ? (
                <div className="py-8 text-center text-sm text-gray-400">Chargement...</div>
            ) : filtered.length === 0 ? (
                <Card extra="p-8 text-center text-sm text-gray-400"><span>Aucun template</span></Card>
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {filtered.map((tpl) => {
                        const status = getApprovalStatus(tpl);
                        const canSubmit = !tpl.isDefault && (!tpl.approval || tpl.approval.status === 'unsubmitted');
                        return (
                            <div
                                key={tpl.sid}
                                className="group flex flex-col rounded-[20px] bg-white p-4 shadow-sm dark:bg-navy-800"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate font-bold text-navy-700 dark:text-white" title={tpl.friendlyName}>
                                            {tpl.friendlyName}
                                        </div>
                                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-400">
                                            <span className="uppercase">{tpl.language}</span>
                                            {tpl.approval?.category && <span>· {tpl.approval.category.toLowerCase()}</span>}
                                            {tpl.isDefault ? (
                                                <span>· par défaut</span>
                                            ) : (
                                                <span>· {new Date(tpl.dateCreated).toLocaleDateString('fr-FR')}</span>
                                            )}
                                        </div>
                                    </div>
                                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}>
                                        {status.label}
                                    </span>
                                </div>

                                <div className="mt-3 line-clamp-5 whitespace-pre-wrap rounded-lg bg-gray-50 p-2 text-xs text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                                    {tpl.body || <span className="italic text-gray-400">{tpl.contentType}</span>}
                                </div>

                                {tpl.approval?.status === 'rejected' && tpl.approval.rejectionReason && (
                                    <div className="mt-2 rounded bg-red-50 px-2 py-1 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-300">
                                        {tpl.approval.rejectionReason}
                                    </div>
                                )}

                                <div className="flex-1" />
                                <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-navy-700">
                                    {canSubmit && (
                                        <>
                                            <select
                                                value={submitCategory[tpl.sid] || 'MARKETING'}
                                                onChange={(e) => setSubmitCategory((prev) => ({ ...prev, [tpl.sid]: e.target.value }))}
                                                className="rounded border border-gray-300 bg-white px-1.5 py-1 text-xs dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                            >
                                                {CATEGORIES.map((c) => (
                                                    <option key={c.value} value={c.value}>{c.label}</option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={() => handleSubmit(tpl)}
                                                disabled={submittingSid === tpl.sid}
                                                className="flex items-center gap-1 rounded bg-green-500 px-2 py-1 text-xs text-white hover:bg-green-600 disabled:opacity-50"
                                            >
                                                <MdSend /> {submittingSid === tpl.sid ? 'Envoi...' : 'Soumettre'}
                                            </button>
                                        </>
                                    )}
                                    <span className="ml-auto font-mono text-[10px] text-gray-300 dark:text-gray-500">{tpl.sid}</span>
                                    {tpl.isDefault ? null : deleteConfirm === tpl.sid ? (
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => handleDelete(tpl.sid)}
                                                className="rounded bg-red-500 px-1.5 py-0.5 text-xs text-white"
                                            >
                                                Oui
                                            </button>
                                            <button
                                                onClick={() => setDeleteConfirm(null)}
                                                className="rounded bg-gray-400 px-1.5 py-0.5 text-xs text-white"
                                            >
                                                Non
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setDeleteConfirm(tpl.sid)}
                                            title="Supprimer"
                                            className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                                        >
                                            <MdDelete />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
